/**
 * Encryption utilities for secure backup encryption using Web Crypto API
 * Uses PBKDF2 for key derivation and AES-GCM for encryption
 */

export interface EncryptedData {
  ciphertext: string; // Base64 encoded
  iv: string; // Base64 encoded initialization vector
  salt: string; // Base64 encoded salt for key derivation
  version: string; // Encryption version for future compatibility
}

/**
 * Derives a cryptographic key from a passphrase using PBKDF2
 */
async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passphraseKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: 100000, // OWASP recommended minimum
      hash: 'SHA-256',
    },
    passphraseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypts data using AES-GCM with a user-provided passphrase
 */
export async function encryptData(data: string, passphrase: string): Promise<EncryptedData> {
  if (!passphrase || passphrase.length < 8) {
    throw new Error('Passphrase must be at least 8 characters long');
  }

  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM

  // Derive encryption key
  const key = await deriveKey(passphrase, salt);

  // Encrypt data
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encodedData
  );

  // Convert to base64 for storage/transmission
  return {
    ciphertext: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv.buffer),
    salt: arrayBufferToBase64(salt.buffer),
    version: '1.0',
  };
}

/**
 * Decrypts data using AES-GCM with a user-provided passphrase
 */
export async function decryptData(encrypted: EncryptedData, passphrase: string): Promise<string> {
  if (!passphrase) {
    throw new Error('Passphrase is required for decryption');
  }

  // Convert from base64
  const ciphertext = base64ToArrayBuffer(encrypted.ciphertext);
  const iv = base64ToArrayBuffer(encrypted.iv);
  const salt = base64ToArrayBuffer(encrypted.salt);

  // Derive decryption key
  const key = await deriveKey(passphrase, new Uint8Array(salt));

  try {
    // Decrypt data
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      ciphertext
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch {
    throw new Error('Decryption failed. Invalid passphrase or corrupted data.');
  }
}

/**
 * Validates passphrase strength
 */
export function validatePassphrase(passphrase: string): {
  valid: boolean;
  strength: 'weak' | 'medium' | 'strong';
  message: string;
} {
  if (passphrase.length < 8) {
    return {
      valid: false,
      strength: 'weak',
      message: 'Passphrase must be at least 8 characters long',
    };
  }

  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  let score = 0;

  // Length scoring
  if (passphrase.length >= 12) score += 1;
  if (passphrase.length >= 16) score += 1;

  // Character variety
  if (/[a-z]/.test(passphrase)) score += 1;
  if (/[A-Z]/.test(passphrase)) score += 1;
  if (/[0-9]/.test(passphrase)) score += 1;
  if (/[^a-zA-Z0-9]/.test(passphrase)) score += 1;

  if (score >= 5) strength = 'strong';
  else if (score >= 3) strength = 'medium';

  return {
    valid: true,
    strength,
    message:
      strength === 'strong'
        ? 'Strong passphrase'
        : strength === 'medium'
        ? 'Medium strength. Consider adding more variety.'
        : 'Weak passphrase. Use a mix of letters, numbers, and symbols.',
  };
}

/**
 * Generates a secure random passphrase
 */
export function generateSecurePassphrase(length: number = 16): string {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  const randomValues = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(randomValues)
    .map((value) => charset[value % charset.length])
    .join('');
}

// Helper functions for base64 encoding/decoding
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Estimates the time to crack a passphrase (educational purposes)
 */
export function estimateCrackTime(passphrase: string): string {
  const charset = new Set(passphrase).size;
  const combinations = Math.pow(charset, passphrase.length);
  const guessesPerSecond = 1e9; // Assume 1 billion guesses/sec (modern GPU)
  const seconds = combinations / guessesPerSecond / 2; // Average case

  if (seconds < 60) return 'Less than a minute';
  if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hours`;
  if (seconds < 31536000) return `${Math.round(seconds / 86400)} days`;
  if (seconds < 31536000 * 100) return `${Math.round(seconds / 31536000)} years`;
  return 'Centuries';
}
